import { Component, OnInit } from '@angular/core';
import { Timespan, DashboardKpiService, SalesSummaryEntity } from 'gung-dashboard';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  AuthService,
  CustomPageTemplateComponent,
  TemplateComponent,
  GungFlowService,
  AssortmentService,
  CustomPageConfigService
} from 'gung-standard';
import { forkJoin, of } from 'rxjs';
import { ColmecLastOrderListConfigService } from '../../services/colmec-last-order-list-config.service';
import { ColmecTopProductListByIdsConfigService } from '../../services/colmec-top-product-list-by-ids-config.service';
import { first } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

const dataDefault: TemplateComponent = {
  templateName: null,
  elements: {
    block1_big_text1: {
      link: {
        enabled: false,
        internal: true,
        url: ''
      },
      image: {
        enabled: false,
        sourceUrl: undefined
      },
      text: {
        enabled: true,
        text: 'NEW_RETREADED_TIRES_RIMS'
      }
    },
    block1_normal_text1: {
      link: {
        enabled: false,
        internal: true,
        url: ''
      },
      image: {
        enabled: false,
        sourceUrl: undefined
      },
      text: {
        enabled: true,
        text: 'USED_TIRES_ARE_MOST'
      }
    },
    block1_normal_red_text1: {
      link: {
        enabled: false,
        internal: true,
        url: ''
      },
      image: {
        enabled: false,
        sourceUrl: undefined
      },
      text: {
        enabled: true,
        text: 'FOR_THEIR_DURABILITY'
      }
    },
    block2_big_text1: {
      link: {
        enabled: false,
        internal: true,
        url: ''
      },
      image: {
        enabled: true,
        sourceUrl: '/assets/colmec_blue.jpg'
      },
      text: {
        enabled: true,
        text: 'TRUST_PERFORMANCE_GREENER_CHOICE'
      }
    },
    block2_big_text2: {
      link: {
        enabled: false,
        internal: true,
        url: ''
      },
      image: {
        enabled: false,
        sourceUrl: undefined
      },
      text: {
        enabled: true,
        text: 'HIGH_QUALITY_RETREADED_TIRES'
      }
    },
    block2_normal_text1: {
      link: {
        enabled: false,
        internal: true,
        url: ''
      },
      image: {
        enabled: false,
        sourceUrl: undefined
      },
      text: {
        enabled: true,
        text: 'HEADLINE_TRUST_FEEL'
      }
    },
    block2_normal_text2: {
      link: {
        enabled: false,
        internal: true,
        url: ''
      },
      image: {
        enabled: false,
        sourceUrl: undefined
      },
      text: {
        enabled: true,
        text: 'TECHNOLOGY_DEVELOPMENT_QUALITY'
      }
    },
    block2_button_1: {
      link: {
        enabled: true,
        internal: true,
        url: '/categories/sNEWPRODUCT'
      },
      image: {
        enabled: false,
        sourceUrl: undefined
      },
      text: {
        enabled: true,
        text: 'DISCOVERY_PREMIUM_TIRE'
      }
    },
    block3_big_text: {
      link: {
        enabled: false,
        internal: true,
        url: ''
      },
      image: {
        enabled: false,
        sourceUrl: undefined
      },
      text: {
        enabled: true,
        text: 'FULL_TITLE_TEXT'
      }
    },
    block3_normal_text: {
      link: {
        enabled: false,
        internal: true,
        url: ''
      },
      image: {
        enabled: false,
        sourceUrl: undefined
      },
      text: {
        enabled: true,
        text: 'SUBTITLE_TEXT'
      }
    }
  }
};

@Component({
    selector: 'colmec-startup-custom-page-template',
    templateUrl: './colmec-startup-custom-page-template.component.html',
    styleUrls: ['./colmec-startup-custom-page-template.component.scss'],
    standalone: false
})
export class ColmecStartupCustomPageTemplateComponent extends CustomPageTemplateComponent implements OnInit {
  timespan = Timespan;
  currentTimespan: Timespan;
  currentGroupOn: SalesSummaryEntity;

  productIds: string[] = [];
  currentUser;
  onlyUserPermission = true;

  constructor(
    public colmecTopProductListByIdsConfigService: ColmecTopProductListByIdsConfigService,
    public colmecLastOrderListConfigService: ColmecLastOrderListConfigService,
    protected dashboardKpiService: DashboardKpiService,
    protected authService: AuthService,
    protected modalService: NgbModal,
    protected router: Router,
    protected gungFlowService: GungFlowService,
    protected assortmentService: AssortmentService,
    translateService: TranslateService,
    protected customPageConfigService: CustomPageConfigService
  ) {
    super(router, modalService, gungFlowService, assortmentService, translateService, customPageConfigService);
  }

  ngOnInit(): void {
    this.currentGroupOn = SalesSummaryEntity.PRODUCT;
    this.currentTimespan = this.timespan.Year;

    const isToShowPreviousTimeSpan = this.getIsShowPreviousTimeSpan();

    const dateSpan = this.dashboardKpiService.getDateFromTimespan(this.currentTimespan);
    const previousDateSpan = this.dashboardKpiService.getDateFromTimespan(this.getPreviousTimeSpan());

    forkJoin({
      user: this.authService.getCurrentUser().pipe(first()),
      salesSummaryGroups: this.dashboardKpiService
        .getSalesSummary(this.currentGroupOn, dateSpan.fromDate, dateSpan.toDate)
        .pipe(first()),
      previousSalesSummaryGroups: isToShowPreviousTimeSpan
        ? this.dashboardKpiService
            .getSalesSummary(this.currentGroupOn, previousDateSpan.fromDate, previousDateSpan.toDate)
            .pipe(first())
        : of(null)
    }).subscribe(({ user, salesSummaryGroups, previousSalesSummaryGroups }) => {
      const top5 = salesSummaryGroups.sort((a, b) => (a.totalAmount < b.totalAmount ? 1 : -1)).slice(0, 6);
      this.productIds = top5.map(p => p.id);
      this.colmecTopProductListByIdsConfigService.setSubscriptionIds(this.productIds);
      if (user.roles.includes('USER') && user.roles.length === 1) {
        this.onlyUserPermission = true;
      } else {
        this.onlyUserPermission = false;
      }
      this.currentUser = user;
    });
  }

  getDefaultData(): TemplateComponent {
    return dataDefault;
  }

  private getPreviousTimeSpan(): Timespan {
    switch (this.currentTimespan) {
      case Timespan.Year:
        return Timespan.LastYear;

      case Timespan.Month:
        return Timespan.LastMonth;

      case Timespan.Week:
        return Timespan.LastWeek;
    }

    return null;
  }

  private getIsShowPreviousTimeSpan(): boolean {
    return (
      this.currentTimespan === Timespan.Year ||
      this.currentTimespan === Timespan.Week ||
      this.currentTimespan === Timespan.Month
    );
  }
}
